import React from "react";
import { observer } from "mobx-react";
import { LetterAvatar, LinkedInBadge } from "./ContactIdentity";

const RELEVANCE_STYLES = {
  CURRENT_MATCH: { bg: "#dcfce7", color: "#166534", label: "Current Role Match" },
  PAST_EXPERIENCE_MATCH: { bg: "#dbeafe", color: "#1e40af", label: "Past Experience Match" },
  ADJACENT_MATCH: { bg: "#fef9c3", color: "#854d0e", label: "Adjacent Match" }
};

// LinkedIn returns the title as "Job Title at Company" — match the popup
// subheader's separator style so left and right sides read the same.
const formatTitleSeparator = (raw) =>
  typeof raw === "string" ? raw.replace(/\s+at\s+/i, " · ") : raw;

const ProfileSummary = observer(({ profileData, structuredProfile, dataQuality, contactLinkedinUrl }) => {
  const firstName = profileData?.firstName || "";
  const lastName = profileData?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const subTitle = formatTitleSeparator(profileData?.subTitle || "");
  const summary = profileData?.user_insights?.summary || "";
  const location = profileData?.location || "";
  const linkedInUrl =
    contactLinkedinUrl ||
    profileData?.linkedInUrl || profileData?.linkedinUrl || profileData?.linkedin_url || "";

  const relevanceRaw = structuredProfile?.seller_relevance || "";
  const relevanceType = relevanceRaw.split(" ")[0]?.replace("—", "").trim();
  const relevanceStyle = RELEVANCE_STYLES[relevanceType] || null;

  const qualityKey = dataQuality?.quality || "";
  const qualityColor = qualityKey === "rich" ? "#059669"
    : qualityKey === "moderate" ? "#d97706" : "#dc2626";
  const qualityLabel = qualityKey
    ? `Research quality: ${qualityKey}${dataQuality?.score != null ? ` (${dataQuality.score})` : ""}`
    : "Research quality";

  return (
    <div className="drSection drProfileSection">
      <div className="drProfileHeader">
        <LetterAvatar seed={fullName || subTitle} size="sm" />
        <div className="drProfileInfo">
          <div className="drProfileNameRow">
            <h2 className="drProfileName">{fullName}</h2>
            {linkedInUrl && <LinkedInBadge url={linkedInUrl} />}
          </div>
          {subTitle && <p className="drProfileTitle">{subTitle}</p>}
          {location && (
            <p className="drProfileLocation">
              📍 {typeof location === "string" ? location : location.default || ""}
            </p>
          )}
        </div>
        <div className="drProfileBadges">
          {qualityKey && (
            <span
              className="drQualityDot"
              title={qualityLabel}
              aria-label={qualityLabel}
              style={{ backgroundColor: qualityColor }}
            />
          )}
        </div>
      </div>

      {summary && <p className="drProfileSummary">{summary}</p>}

      {relevanceStyle && (
        <div className="drRelevanceBadge" style={{ backgroundColor: relevanceStyle.bg, color: relevanceStyle.color }}>
          {relevanceStyle.label}: {relevanceRaw.replace(relevanceType, "").replace("—", "").trim()}
        </div>
      )}

      {structuredProfile && Object.keys(structuredProfile).length > 0 && (
        <div className="drProfileDetails">
          {structuredProfile.persona_type && (
            <div className="drDetailRow">
              <span className="drDetailLabel">Persona</span>
              <span className="drDetailValue">{structuredProfile.persona_type}</span>
            </div>
          )}
          {structuredProfile.kpi_focus && (
            <div className="drDetailRow">
              <span className="drDetailLabel">KPI Focus</span>
              <span className="drDetailValue">{structuredProfile.kpi_focus}</span>
            </div>
          )}
          {structuredProfile.skepticism_profile && (
            <div className="drDetailRow">
              <span className="drDetailLabel">Will Push Back On</span>
              <span className="drDetailValue">{structuredProfile.skepticism_profile}</span>
            </div>
          )}
          {structuredProfile.disc_type && (
            <div className="drDetailRow">
              <span className="drDetailLabel">DISC Type</span>
              <span className="drDetailValue">{structuredProfile.disc_type}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ProfileSummary;
